import type OLCesium from "olcs";

import Map from "./Map";
import ElevationProvider from "./ElevationProvider.js";
import VectorSource from "ol/source/Vector.js";
import VectorLayer from "ol/layer/Vector.js";
import LayerGroup from "ol/layer/Group.js";
import OlMap from "ol/Map.js";
import View from "ol/View.js";
import {defaults as defaultControls} from "ol/control/defaults.js";
import {Attribution, Control} from "ol/control.js";
import Container from "./Container.js";
import {Collection as olCollection} from "ol";

export default class Map3d {
    private ol3d: OLCesium|null = null;
    private readonly initializePromise: Promise<void>;
    private static OlCesiumPackage: typeof OLCesium = null;

    private openlayersMap: OlMap|null = null;
    private mapView: View|null = null;
    private mapImageLayerGroup: LayerGroup|null = null;
    private cutoutsVectorSource: VectorSource<any>|null = null;
    private cutoutsVectorLayer: VectorLayer<any>|null = null;
    private routesVectorSource: VectorSource<any>|null = null;
    private routesVectorLayer: VectorLayer<any>|null = null;
    private locationsVectorSource: VectorSource<any>|null = null;
    private locationsVectorLayer: VectorLayer<any>|null = null;
    private shapesVectorSource: VectorSource<any>|null = null;
    private shapesVectorLayer: VectorLayer<any>|null = null;
    private map3dControl: Control|null = null;


    constructor(readonly map2d: Map, private readonly elevationProvider: ElevationProvider, private readonly html_el_id: string) {
        this.initializePromise = (async () => {
            await this.elevationProvider.awaitInitialization();

            if (Map3d.OlCesiumPackage === null) {
                Map3d.OlCesiumPackage = (await import('olcs')).default; // import OLCesium from "olcs";
            }
        })();
    }

    async buildOpenLayersMap() {
        this.cutoutsVectorSource = new VectorSource({});

        this.cutoutsVectorLayer = new VectorLayer({
            source: this.cutoutsVectorSource,
            style: function(feature, resolution) {
                if (feature.get('custom_style')) {
                    return feature.get('custom_style')();
                }
                return null;
            },
        });

        this.routesVectorSource = new VectorSource({});

        this.routesVectorLayer = new VectorLayer({
            source: this.routesVectorSource,
        });

        this.locationsVectorSource = new VectorSource({});

        this.locationsVectorLayer = new VectorLayer({
            source: this.locationsVectorSource,
        });

        this.shapesVectorSource = new VectorSource({});

        this.shapesVectorLayer = new VectorLayer({
            source: this.shapesVectorSource,
        });

        // Position features correctly on 3D map
        this.cutoutsVectorLayer.set('altitudeMode', 'clampToGround');
        this.routesVectorLayer.set('altitudeMode', 'clampToGround');
        this.locationsVectorLayer.set('altitudeMode', 'clampToGround');
        this.shapesVectorLayer.set('altitudeMode', 'clampToGround');

        this.mapImageLayerGroup = new LayerGroup();

        if (!Container.mapImageProvider(this.map2d.currentInterfaceMapMipName).supports3D()) {
            alert('Deze kaartbron ondersteunt geen 3D weergave');
        } else {
            const mip = Container.mapImageProvider(this.map2d.currentInterfaceMapMipName);
            const layers = await this.map2d.buildMipLayersForOpenLayers(mip);

            this.mapImageLayerGroup.setLayers(new olCollection(layers));
        }

        this.mapView = new View({
            center: this.map2d.getOpenlayersMap().getView().getCenter(),
            zoom: this.map2d.getOpenlayersMap().getView().getZoom(),
            rotation: this.map2d.getOpenlayersMap().getView().getRotation(),
            constrainResolution: true,
        });

        this.openlayersMap = new OlMap({
            layers: [
                this.mapImageLayerGroup,
                this.cutoutsVectorLayer,
                this.routesVectorLayer,
                this.locationsVectorLayer,
                this.shapesVectorLayer,
            ],
            view: this.mapView,
            target: this.html_el_id,
            controls: defaultControls({attribution: false}).extend([new Attribution({collapsible: false})]),
        });

        const toggleMap3d = document.createElement('div');
        toggleMap3d.className = 'ol-control ol-unselectable ol-toggle-map3d-button';
        toggleMap3d.innerHTML = '<button title="3D uitschakelen" class="ol-toggle-map3d"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-badge-3d" viewBox="0 0 16 16">' +
            '  <path d="M4.52 8.368h.664c.646 0 1.055.378 1.06.9.008.537-.427.919-1.086.919-.598-.004-1.037-.325-1.068-.756H3c.03.914.791 1.688 2.153 1.688 1.24 0 2.285-.66 2.272-1.798-.013-.953-.747-1.38-1.292-1.432v-.062c.44-.07 1.125-.527 1.108-1.375-.013-.906-.8-1.57-2.053-1.565-1.31.005-2.043.734-2.074 1.67h1.103c.022-.391.383-.751.936-.751.532 0 .928.33.928.813.004.479-.383.835-.928.835h-.632v.914zm3.606-3.367V11h2.189C12.125 11 13 9.893 13 7.985c0-1.894-.861-2.984-2.685-2.984zm1.187.967h.844c1.112 0 1.621.686 1.621 2.04 0 1.353-.505 2.02-1.621 2.02h-.844z"/>' +
            '  <path d="M14 3a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM2 2a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"/>' +
            '</svg></button>';

        toggleMap3d.querySelector('.ol-toggle-map3d').addEventListener('click', async (evt) => {
            if (evt.defaultPrevented) {
                return;
            }

            evt.preventDefault();
            evt.stopPropagation();

            await this.hide();
        });
        this.map3dControl = new Control({
            element: toggleMap3d,
        });
        this.openlayersMap.addControl(this.map3dControl);
    }

    async syncMapFrom2d() {
        const map2dView = this.map2d.getOpenlayersMap().getView();

        this.mapView.setCenter(map2dView.getCenter());
        this.mapView.setZoom(map2dView.getZoom());
        this.mapView.setRotation(map2dView.getRotation());

        this.cutoutsVectorSource.clear();
        this.routesVectorSource.clear();
        this.locationsVectorSource.clear();
        this.shapesVectorSource.clear();

        const cutouts = [];
        for (const cutout of this.map2d.userInterface.getCutouts()) {
            if (cutout.visibleOnMap) {
                cutouts.push(cutout.openlayersPolygonFeature.clone());
            }
        }
        this.cutoutsVectorSource.addFeatures(cutouts);

        const routeCollection = this.map2d.userInterface.getRouteCollection();
        const routes = [];
        for (const route of [...routeCollection.getRoutes(), routeCollection.getSketchRoute()]) {
            // (ol-)cesium cannot handle routes with less than 2 coordinates
            if (route.getCoordinates().length < 2) {
                continue;
            }

            if (route.isVisible()) {
                routes.push(route.mainFeature.clone());
            }
        }
        this.routesVectorSource.addFeatures(routes);

        const locations = [];
        for (const location of this.map2d.userInterface.getLocationCollection().getLocations()) {
            if (location.isVisible()) {
                locations.push(location.mapFeature.clone());
            }
        }
        this.locationsVectorSource.addFeatures(locations);

        const shapeCollection = this.map2d.userInterface.getShapeCollection();
        const shapes = [];
        for (const shape of shapeCollection.getShapes()) {
            // (ol-)cesium cannot handle shapes with less than 2 coordinates
            if (shape.getCoordinates().length < 2) {
                continue;
            }

            if (shape.isVisible()) {
                shapes.push(shape.mainFeature.clone());
            }
        }
        this.shapesVectorSource.addFeatures(shapes);
    }

    sync2dMap() {
        const map2dView = this.map2d.getOpenlayersMap().getView();

        map2dView.setCenter(this.mapView.getCenter());
        map2dView.setZoom(this.mapView.getZoom());
        map2dView.setRotation(this.mapView.getRotation());
    }

    private async ensureCreated() {
        if (this.openlayersMap === null) {
            await this.buildOpenLayersMap();
        }

        await this.initializePromise;

        if (this.ol3d === null) {
            this.ol3d = new (Map3d.OlCesiumPackage)({
                map: this.openlayersMap,
            })

            this.ol3d.getCesiumScene().terrainProvider = this.elevationProvider.getTerrainProvider();
            this.ol3d.getCesiumScene().globe.depthTestAgainstTerrain = true; // Improves location marker visibility
        }
    }

    async show() {
        await this.ensureCreated();

        this.map2d.userInterface.reactiveData.userInterfaceOptions.isMap3dEnabled = true; // TODO: do not reference reactiveData directly
        document.body.classList.toggle('is-map-3d-enabled', true);

        this.map2d.userInterface.getRouteCollection().unfocus();
        this.map2d.userInterface.getRouteCollection().toggleRouteDetailsPanelForRoute(null);
        this.map2d.userInterface.getShapeCollection().unfocus();
        this.map2d.userInterface.closeCoordinatePanel();
        this.map2d.userInterface.closeGeneralDropdownMenu();
        this.map2d.userInterface.closeInterfaceMapSource();

        await this.syncMapFrom2d();

        this.ol3d.setEnabled(true);
    }

    async hide() {
        this.sync2dMap();

        this.ol3d.setEnabled(false);
        this.map2d.userInterface.reactiveData.userInterfaceOptions.isMap3dEnabled = false; // TODO: do not reference reactiveData directly
        document.body.classList.toggle('is-map-3d-enabled', false);
    }

    destroy() {
        if (this.ol3d !== null) {
            this.ol3d.destroy();
            this.ol3d = null;
        }

        if (this.openlayersMap !== null) {
            document.getElementById(this.html_el_id).innerHTML = '';
            this.openlayersMap = null;

            this.mapView = null;
            this.mapImageLayerGroup = null;
            this.cutoutsVectorSource = null;
            this.cutoutsVectorLayer = null;
            this.routesVectorSource = null;
            this.routesVectorLayer = null;
            this.locationsVectorSource = null;
            this.locationsVectorLayer = null;
            this.shapesVectorSource = null;
            this.shapesVectorLayer = null;
            this.map3dControl = null;
        }
    }
}
